import { API_BASE_URL, getBaseHeaders } from './api.config';
import { throwApiError } from './api.error';
import { Chat, ChatMessage, ChatMessagesCursorPage, ChatProductDetail, PaginatedResponse } from './api.types';
import { authenticatedCall } from './auth';

export const MAX_CHAT_MESSAGE_LENGTH = 1000;
export const MAX_PRODUCT_DETAIL_MESSAGE_LENGTH = 500;
export type ChatConversationRole = 'SELLER' | 'BUYER';

type CompatibleCrypto = Pick<Crypto, 'getRandomValues'> & Partial<Pick<Crypto, 'randomUUID'>>;

export const createClientMessageId = (
  cryptoApi: CompatibleCrypto = globalThis.crypto,
): string => {
  if (typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export type UserChatsResponse = PaginatedResponse<Chat>;

export interface SendMessageRequest {
  message: string;
  productId: string;
  clientMessageId: string;
}

export interface SendMessageResponse {
  id: string;
}

export interface ReplyToChatRequest {
  message: string;
  clientMessageId: string;
}

export interface ChatMessagesResponse {
  id: string;
  messages: ChatMessagesCursorPage;
  viewedStatus: 'VIEWED' | 'NOT_VIEWED';
}

export interface UnreadChatsResponse {
  count: number;
  hasUnread: boolean;
}

export const sendMessage = async (
  data: SendMessageRequest
): Promise<SendMessageResponse> => {
  validateMessageLength(data.message, MAX_PRODUCT_DETAIL_MESSAGE_LENGTH);

  const response = await authenticatedCall(
    `${API_BASE_URL}/chats/communicate`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const fetchUserChats = async (
  page: number = 1,
  size: number = 10,
  productId: string | null = null,
  searchTerm: string = '',
  conversationRole: ChatConversationRole | null = null
): Promise<UserChatsResponse> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/chats/search?page=${page}&size=${size}&sortField=LAST_MESSAGE_AT&order=DESC`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        productId,
        searchTerm: searchTerm || null,
        conversationRole
      })
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const fetchUnreadChats = async (): Promise<UnreadChatsResponse> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/chats/unread`,
    {
      method: 'GET',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const fetchChatMessages = async (
  chatId: string,
  size: number = 10,
  beforeCreatedAt: string | null = null
): Promise<ChatMessagesResponse> => {
  const params = new URLSearchParams({
    size: size.toString()
  });

  if (beforeCreatedAt) {
    params.set('beforeCreatedAt', beforeCreatedAt);
  }

  const response = await authenticatedCall(
    `${API_BASE_URL}/chats/${chatId}/messages?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const fetchChatProduct = async (chatId: string): Promise<ChatProductDetail> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/chats/${chatId}/product`,
    {
      method: 'GET',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const markChatAsViewed = async (chatId: string): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/chats/${chatId}/viewed`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const replyToChat = async (
  chatId: string,
  data: ReplyToChatRequest
): Promise<ChatMessage> => {
  validateMessageLength(data.message, MAX_CHAT_MESSAGE_LENGTH);

  const response = await authenticatedCall(
    `${API_BASE_URL}/chats/${chatId}/reply`,
    {
      method: 'PUT',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

const validateMessageLength = (message: string, maxLength: number): void => {
  if (message.length > maxLength) {
    throw new Error(`Mesajul poate avea maximum ${maxLength} de caractere.`);
  }
};

export const deleteChat = async (chatId: string): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/chats/${chatId}`,
    {
      method: 'DELETE',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const reportChat = async (
  chatId: string,
  reasons: string[]
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/chats/${chatId}/report`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ reasons })
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};
